import $http from '../services/AxiosInstance';
import { ErrorApi } from '../types/api.type';
import {
  IAudit,
  ICompanyGPS,
  IZipCode,
  ITypeContainer,
  IAssetsLanding,
} from '../types/landing.type';
import { downloadPDFBase64 } from '../helpers';
import getSubDomainInitial from '../helpers/getSubDomainInitial';
import { IClient } from '../types/client.type';
import { LocationQueryValue } from 'vue-router';
import { IFinalCalculation } from 'src/helpers/finalCalculationAR';
import { ICountry } from '../types/countries.type';

enum HeaderType {
  AR = 'AR',
  CL = 'CL',
  MX = 'MX',
}

interface ResGetAssets extends ErrorApi {
  assets?: Array<IAssetsLanding>;
  nombre?: string;
  value?: number;
}

interface ResCreateAudit extends ErrorApi {
  assets?: Array<IAssetsLanding>;
  nombre?: string;
  value?: number;
}

interface ResFindClientByTributaryCode extends ErrorApi {
  locations?: ResZipCodes | null;
  client?: IClient | null;
}

interface ResCompanyGPS extends ErrorApi {
  companiesGPS?: ICompanyGPS[];
}

interface ResZipCodes extends ErrorApi {
  data?: IZipCode[];
  message?: string;
}

const getAssets = async (
  country: string | null | undefined = null,
): Promise<ResGetAssets> => {
  try {
    const params = country ? { params: { country } } : {};
    const { data } = await $http.get('/assets', params);
    const assets = data.data.map((asset: IAssetsLanding) => ({
      nombre: asset.description,
      id: asset.id,
      riskType: asset.riskType,
    }));
    return assets;
  } catch (error: any) {
    return { error };
  }
};

const getSubAssets = async (assetId: number): Promise<ResGetAssets[]> => {
  try {
    const { data } = await $http.get(`/sub-assets/?assetId=${assetId}`);
    const assets = data.subAssets.map((asset: IAssetsLanding) => ({
      nombre: asset.description,
      id: asset.id,
      riskType: asset.riskType,
    }));

    return assets;
  } catch (error: any) {
    throw error;
  }
};

const getProducts = async (subAssetId: number): Promise<ResGetAssets> => {
  try {
    const { data } = await $http.get(`/products/?subAssetId=${subAssetId}`);

    const products = data.map((product: IAssetsLanding) => ({
      nombre: product.description,
      id: product.id,
      riskType: product.riskType,
    }));

    return products;
  } catch (error: any) {
    return { error };
  }
};

const createAudits = async (params: IAudit): Promise<ResCreateAudit> => {
  try {
    const { data } = await $http.post('/audits', params);
    return data;
  } catch (error: any) {
    return { error };
  }
};

const findClient = async (email: string): Promise<IClient> => {
  try {
    const { data } = await $http.get('/clients/search', {
      params: {
        email: email,
      },
    });
    return data;
  } catch (error: any) {
    throw error;
  }
};

const findZipCode = async (
  zipCode: number,
  isForSelect: boolean,
): Promise<ResZipCodes> => {
  try {
    const { data: location } = await $http.get('/chubb/location', {
      params: {
        zipCode: zipCode,
        countryId: 1
      },
    });

    let locations = location;

    if (isForSelect) {
      locations = location.data.map((loc: any) => ({
        nombre: loc.ColoniaDsc,
        id: loc.id,
      }));
    }
    return locations;
  } catch (error: any) {
    return { error };
  }
};

const findClientByTributaryCode = async (
  code: string,
  country: string | null = null,
): Promise<ResFindClientByTributaryCode> => {
  try {
    const countryInitial = country ?? getSubDomainInitial();
    const { data } = await $http.get(`/clients/searchClient/${code}`);

    if (countryInitial === 'MEXICO') {
      if (!data.data) return { locations: null, client: null };

      if (!data.data.postCode || !data.data.neighborhood) {
        return { locations: null, client: data.data };
      }

      const locations = await findZipCode(data.data.postCode, true);

      return { locations, client: data.data };
    }

    return { client: data.data };
  } catch (error: any) {
    return { error };
  }
};

const findCompaniesGPS = async (): Promise<ICompanyGPS[]> => {
  try {
    const { data } = await $http.get('/companiesGPS/all');
    const companiesGPS = data.map((companyGPS: ICompanyGPS) => ({
      nombre: companyGPS.description,
      slug: companyGPS.slug,
      id: companyGPS.id,
    }));

    return companiesGPS;
  } catch (error: any) {
    return { error } as any;
  }
};

const findTypesContainers = async (): Promise<ITypeContainer[]> => {
  try {
    const { data: response } = await $http.get('/typeContainers');

    const typesContainers = response.data.typeContainers.map(
      (typeContainer: ITypeContainer) => ({
        nombre: typeContainer.typeName,
        id: typeContainer.id,
      }),
    );

    return typesContainers;
  } catch (error: any) {
    return { error } as any;
  }
};
const findCountries = async (): Promise<ICountry[]> => {
  try {
    const { data: response } = await $http.get('/countries');

    const countries = response.data.countries.map((country: ICountry) => ({
      nombre: country.description,
      id: country.id,
    }));

    return countries;
  } catch (error: any) {
    return { error } as any;
  }
};

const createOrUpdateClient = async (
  id: string | undefined,
  body: any,
  headerType: keyof typeof HeaderType,
) => {
  try {
    const header = {
      headers: {
        xcountry: headerType,
      },
    };
    if (!id) {
      const { data } = await $http.post('/clients', body, header);
      return data;
    } else {
      const { data } = await $http.put(`/clients/${id}`, body, header);
      return data;
    }
  } catch (error: any) {
    return { error };
  }
};

const createPolicy = async (clientId: number, MagenerId: number, body: any, xcountry: string = 'MX') => {
  const formData = new FormData();

  // Agregar cada campo del objeto createPolicyData al FormData
  for (const key in body) {
    if (body.hasOwnProperty(key)) {
      const value = body[key];

      // Evitar agregar 'null' o 'undefined' como string
      if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    }
  }

  const policy = await $http.post(
    `/clients/${clientId}/managers/${MagenerId}/policies`,
    formData,
    {
      headers: {
        xcountry,
      },
    },
  );

  return policy;
};
const createPolicyMaritime = async (
  clientId: number,
  MagenerId: number,
  body: any,
  headerType: keyof typeof HeaderType = 'MX',
) => {
  const formData = new FormData();

  // Agregar cada campo del objeto createPolicyData al FormData
  for (const key in body) {
    if (body.hasOwnProperty(key)) {
      const value = body[key];

      // Evitar agregar 'null' o 'undefined' como string
      if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    }
  }

  const policy = await $http.post(
    `/clients/${clientId}/managers/${MagenerId}/policies-maritimes`,
    formData,
    {
      headers: {
        xcountry: headerType,
      },
    },
  );

  return policy.data.id;
};

const createPolicyPackage = async (
  clientId: number,
  MagenerId: number,
  body: any,
  headerType: keyof typeof HeaderType = 'MX',
) => {
  const formData = new FormData();

  // Agregar cada campo del objeto createPolicyData al FormData
  for (const key in body) {
    if (body.hasOwnProperty(key)) {
      const value = body[key];

      // Evitar agregar 'null' o 'undefined' como string
      if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    }
  }

  const policy = await $http.post(
    `/clients/${clientId}/managers/${MagenerId}/policies-packages`,
    formData,
    {
      headers: {
        xcountry: headerType,
      },
    },
  );

  return policy.data.id;
};

const createPolicyForTrygerLatam = async (
  clientId: number,
  MagenerId: number,
  body: any,
  headerType: keyof typeof HeaderType = 'MX',
) => {
  const formData = new FormData();

  // Agregar cada campo del objeto createPolicyData al FormData
  for (const key in body) {
    if (body.hasOwnProperty(key)) {
      const value = body[key];

      // Evitar agregar 'null' o 'undefined' como string
      if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    }
  }

  const { data } = await $http.post(
    `/clients/${clientId}/managers/${MagenerId}/policies`,
    formData,
    {
      headers: {
        xcountry: headerType,
      },
    },
  );

  return data.paymentHubUrl;
};

const commitPolicyForTrygerLatam = async (
  policyId: string | LocationQueryValue[],
  guid: string | LocationQueryValue[],
  invoiceId: string | LocationQueryValue[],
) => {
  const body = {
    guid,
    invoiceId,
  };

  await $http.put(`/clients/commitPolicies/${policyId}`, body);
};

const getPricing = async (
  indemnificationLimit: number,
  tributaryCode: string,
  regionProvince: string,
): Promise<IFinalCalculation> => {
  const { data } = await $http.post('/clients/pricing', {
    indemnificationLimit,
    tributaryCode,
    regionProvince,
  });

  return data;
};

const getPDFCoverage = async (policyId: any, type: string = 'Terrestre') => {
  try {
    const policyIdString = policyId.toString();
    let response;
    if (type === 'Maritimo') {
      response = await $http.get(
        `/clients/policies/${policyIdString}/pdf-maritime`,
      );
    } else if (type === 'Paqueteria') {
      response = await $http.get(
        `/clients/policies/${policyIdString}/pdf-package`,
      );
    } else {
      response = await $http.get(`/clients/policies/${policyIdString}/pdf`);
    }

    downloadPDFBase64(response, policyIdString);
  } catch (error: any) {
    return { error };
  }
};

const calculatePriceForLanding = async (
  data: any,
): Promise<{
  calculation: number;
  tax: {
    id: number;
    tax: number;
    additionalPrima: number;
    primaMin: number;
    indemnificationLimitMin: number | null;
    indemnificationLimitMax: number | null;
  };
}> => {
  try {
    const response = await $http.post(`/taxes/calculate-price-landing`, data);
    return response.data;
  } catch (error: any) {
    throw Error(error.message);
  }
};

const retryPaymentChile = async (
  order: number,
): Promise<any> => {
  try {
    const response = await $http.post('payments/create-payment-order', { order });
    return response.data.urlPayment
  } catch (error: any) {
    return error
  }
}

export {
  getAssets,
  createAudits,
  findClient,
  findClientByTributaryCode,
  findCompaniesGPS,
  findZipCode,
  createOrUpdateClient,
  createPolicy,
  createPolicyMaritime,
  createPolicyPackage,
  getPDFCoverage,
  createPolicyForTrygerLatam,
  commitPolicyForTrygerLatam,
  getPricing,
  findTypesContainers,
  calculatePriceForLanding,
  getSubAssets,
  getProducts,
  findCountries,
  retryPaymentChile
};
